import { useEffect, useState } from 'react';
import { Moon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Session {
  id: string;
  label: string;
  zone: string;
  start: number;
  end: number;
}

// FX trading-session hours, local to each financial centre (DST-correct via
// IANA zone). NOTE: these are the *forex* session windows — the FX market
// trades 24h on weekdays, not equity-exchange hours. Using narrow stock-
// exchange hours here (e.g. NYSE 09:30–16:00) wrongly reports a session as
// "closed" during active FX hours such as the London/New-York overlap.
const SESSIONS: Session[] = [
  { id: 'syd', label: 'SYD', zone: 'Australia/Sydney',   start: 7,  end: 16 },
  { id: 'tky', label: 'TKY', zone: 'Asia/Tokyo',         start: 9,  end: 18 },
  { id: 'ldn', label: 'LDN', zone: 'Europe/London',      start: 8,  end: 17 },
  { id: 'nyc',  label: 'NY',   zone: 'America/New_York', start: 9.5,  end: 16   },
];

interface ZoneInfo {
  hour: number;
  weekday: string;
}

function hourInZone(zone: string, now: Date): ZoneInfo {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hour: 'numeric', minute: 'numeric', hour12: false, weekday: 'short',
    }).formatToParts(now);
    const h = parseInt(parts.find(p => p.type === 'hour')!.value, 10);
    const m = parseInt(parts.find(p => p.type === 'minute')!.value, 10);
    const weekday = parts.find(p => p.type === 'weekday')!.value;
    return { hour: (h % 24) + (m / 60), weekday };
  } catch {
    return { hour: 0, weekday: 'Mon' };
  }
}

function isWeekend(weekday: string) {
  return weekday === 'Sat' || weekday === 'Sun';
}

function isOpen(s: Session, now: Date) {
  const { hour, weekday } = hourInZone(s.zone, now);
  if (isWeekend(weekday)) return false;
  return hour >= s.start && hour < s.end;
}

export function SessionIndicators() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const states = SESSIONS.map(s => ({ session: s, open: isOpen(s, now) }));
  const anyOpen = states.some(st => st.open);
  // Weekend if every session's local clock falls on Sat/Sun.
  const allWeekend = SESSIONS.every(s => isWeekend(hourInZone(s.zone, now).weekday));

  return (
    <div className="hidden sm:flex items-center gap-1.5">
      {(allWeekend || !anyOpen) && (
        <div
          className="flex items-center gap-1 px-2 py-1 rounded-md text-2xs font-semibold text-bear bg-bear/10"
          title={
            allWeekend
              ? 'Weekend — all FX & equity sessions closed'
              : 'All trading sessions are currently closed'
          }
        >
          <Moon className="size-3" />
          {allWeekend ? 'Weekend' : 'Closed'}
        </div>
      )}
      {states.map(({ session: s, open }) => (
        <div
          key={s.id}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md text-2xs font-semibold transition-colors',
            open
              ? 'bg-bull/10 text-bull'
              : 'bg-bear/10 text-bear',
          )}
          title={
            `${s.label}: ${formatHour(s.start)}–${formatHour(s.end)} local — ` +
            (open ? 'open now' : 'closed')
          }
        >
          <span
            className={cn(
              'size-1.5 rounded-full',
              open ? 'bg-bull animate-pulse-soft' : 'bg-bear',
            )}
          />
          {s.label}
        </div>
      ))}
    </div>
  );
}

function formatHour(h: number) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
